import {Form, FormField, FormSubmitHandler} from 'platform/components';
import {Grid, Hide, VStack} from 'platform/foundation';

import {isNil} from 'ramda';

import {
  CorrectiveTaxDocumentSettingResponseBody,
  CreateCorrectiveTaxDocumentSettingRequestBody,
} from '@dms/api/core';
import {
  useCreateCorrectiveTaxDocumentSettingMutation,
  useUpdateCorrectiveTaxDocumentSettingMutation,
} from '@dms/api/finance';
import i18n from '@dms/i18n';
import {settingsRoutes, testIds} from '@dms/routes';
import {SettingsFooter, SettingsSection} from '@dms/settings/shared';
import {handleApiError, useBranches} from '@dms/shared';

import {ApiError, useNavigate} from 'shared/web';

import {correctiveTaxDocumentFormValidations} from './correctiveTaxDocumentFormValidations';

interface CorrectiveTaxDocumentFormProps {
  correctiveTaxDocumentSetting?: CorrectiveTaxDocumentSettingResponseBody;
}

export const CorrectiveTaxDocumentForm = (props: CorrectiveTaxDocumentFormProps) => {
  const isCreating = isNil(props.correctiveTaxDocumentSetting);
  const navigate = useNavigate();
  const {branchOptions} = useBranches();

  const [createCorrectiveTaxDocumentSetting] = useCreateCorrectiveTaxDocumentSettingMutation();
  const [updateCorrectiveTaxDocumentSetting] = useUpdateCorrectiveTaxDocumentSettingMutation();

  const handleSubmit: FormSubmitHandler<CreateCorrectiveTaxDocumentSettingRequestBody> = async (
    data,
    _setErrors,
    _reset,
    blockerContext
  ) => {
    if (isCreating) {
      return createCorrectiveTaxDocumentSetting({
        createCorrectiveTaxDocumentSettingRequestBody: data,
      })
        .unwrap()
        .then(() => {
          if (blockerContext?.wasSubmittedFromBlocker) {
            return;
          }
          navigate(settingsRoutes.correctiveTaxDocuments);
        })
        .catch((e: ApiError) =>
          handleApiError(e, {signalApiError: blockerContext?.reportApiFailure})
        );
    } else if (props.correctiveTaxDocumentSetting) {
      await updateCorrectiveTaxDocumentSetting({
        id: props.correctiveTaxDocumentSetting.id,
        updateCorrectiveTaxDocumentSettingRequestBody: data,
      })
        .unwrap()
        .then(() => {
          if (blockerContext?.wasSubmittedFromBlocker) {
            return;
          }
          navigate(settingsRoutes.correctiveTaxDocuments);
        })
        .catch((e: ApiError) =>
          handleApiError(e, {signalApiError: blockerContext?.reportApiFailure})
        );
    }
  };

  return (
    <Form<CreateCorrectiveTaxDocumentSettingRequestBody>
      schema={correctiveTaxDocumentFormValidations(!!props.correctiveTaxDocumentSetting?.isSystem)}
      defaultValues={{
        createCorrectiveTaxDocumentType: 'without_proforma',
        ...(props.correctiveTaxDocumentSetting as Partial<CreateCorrectiveTaxDocumentSettingRequestBody>),
      }}
      onSubmit={handleSubmit}
      shouldWatchForUnsavedChanges
    >
      {(control, _formApi, navigateWithoutBlocker) => (
        <SettingsSection>
          <VStack spacing={4}>
            <Grid columns={2}>
              <Hide when={props.correctiveTaxDocumentSetting?.isSystem}>
                <FormField
                  control={control}
                  type="choice"
                  name="branchId"
                  options={branchOptions}
                  label={i18n.t('entity.branch.labels.branch')}
                  data-testid={testIds.settings.correctiveTaxDocumentsEdit('branchId')}
                  isRequired
                />
              </Hide>
              <FormField
                control={control}
                type="choice"
                name="createCorrectiveTaxDocumentType"
                options={[
                  {
                    value: 'without_proforma',
                    label: i18n.t('entity.correctiveTaxDocumentSetting.labels.withoutProforma'),
                  },
                  {
                    value: 'with_proforma',
                    label: i18n.t('entity.correctiveTaxDocumentSetting.labels.withProforma'),
                  },
                ]}
                label={i18n.t(
                  'entity.correctiveTaxDocumentSetting.labels.createCorrectiveTaxDocumentType'
                )}
                data-testid={testIds.settings.correctiveTaxDocumentsEdit(
                  'createCorrectiveTaxDocumentType'
                )}
                isRequired
              />
            </Grid>

            <SettingsFooter
              actions={[
                {
                  variant: 'secondary',
                  type: 'button',
                  onClick: () => navigateWithoutBlocker(settingsRoutes.correctiveTaxDocuments),
                  title: i18n.t('general.actions.cancel'),
                  'data-testid': testIds.settings.correctiveTaxDocumentsEdit('cancel'),
                },
                {
                  type: 'form-button',
                  title: isCreating
                    ? i18n.t('general.actions.create')
                    : i18n.t('general.actions.save'),
                  'data-testid': testIds.settings.correctiveTaxDocumentsEdit('submit'),
                  control,
                  buttonType: 'submit',
                },
              ]}
            />
          </VStack>
        </SettingsSection>
      )}
    </Form>
  );
};
